import { useTranslation } from 'react-i18next';
import InfoIcon from '../../../assets/icons/info.svg';
import { getFormattedDate } from '../../../config/common';
import styled from 'styled-components';
import Tooltip from 'monday-ui-react-core/dist/Tooltip';
import { useContext, useEffect, useRef } from 'react';
import { FileContext } from '../../../context/FileContext';
import { Resizable } from 're-resizable';
import { DEFAULT_HEIGHTS, DEFAULT_WIDTHS } from '../../../constants/pdfOverlayDimensions';
import {
  BIDI_ISOLATE_STYLE,
  resolveTextAlign,
  resolveTextDirection,
} from '../../../utils/common';

const StyledTooltip = styled(Tooltip)`
  width: 355px;
  text-align: center;
`;

const PrefilledFieldContainer = styled.div`
  .inner-container {
    line-height: 1.3;
    white-space: normal;
    word-wrap: break-word;
    font-family: 'Open Sans', sans-serif;
  }
`;

const FormulaColumnContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ align }) => align};
  gap: 5px;

  .icon {
    width: ${({ fontSize }) => fontSize + 'px'};
    height: ${({ fontSize }) => fontSize + 'px'};
    cursor: pointer;
  }
`;

const PrefilledFieldsEditOverlayChip = ({ item, setIsResizing }) => {
  const { t } = useTranslation();
  const {
    columnValuesMap,
    addedFormFields,
    updateFormFields,
    currentSelectedItem,
  } = useContext(FileContext);

  const currentRef = useRef(null);
  const defaultHeight = DEFAULT_HEIGHTS.PREFILLED_FIELD;
  const defaultWidth = DEFAULT_WIDTHS.PREFILLED_FIELD;

  useEffect(() => {
    if (currentRef.current) {
      if (!item?.height) {
        // Use same default height
        item.height = defaultHeight;
      }
      if (!item?.width) {
        item.width = currentRef.current.clientWidth + 3;
      }
    }
  }, [item]);

  const handleResizeStart = (_e, _dir) => {
    setIsResizing(true);
  };

  const handleResizeEnd = (item) => (_e, _dir, _ref, d) => {
    setIsResizing(false);
    item.width = _ref.clientWidth;
    item.height = _ref.clientHeight;
    addedFormFields[currentSelectedItem.current] = item;
    updateFormFields([...addedFormFields]);
  };

  const defaultPreviewer = (title) => {
    return <span className="no-wrap">{`${title} ${t("column's value")}`}</span>;
  };
  const textAlign = resolveTextAlign(item?.fontStyle?.alignment);

  useEffect(() => {
    item.height = currentRef.current.clientHeight;
    const index = addedFormFields.findIndex((x) => x.id === item.id);
    if (index > -1) {
      addedFormFields[index] = item;
      updateFormFields([...addedFormFields]);
    }
  }, [item.fontSize]);


  return (
    <>
      <PrefilledFieldContainer fontSize={item.fontSize}>
        <Resizable
          enable={{
            top: false,
            right: item?.isActive,
            bottom: false,
            left: false,
            topRight: false,
            bottomRight: item?.isActive,
            bottomLeft: false,
            topLeft: false,
          }}
          onResizeStart={handleResizeStart}
          onResizeStop={handleResizeEnd(item)}
        >
          <div
            className="inner-container"
            style={{
              height: item.height || 'fit-content',
              overflowY: 'hidden',
              width: item.width || defaultWidth,
              maxWidth: 500,
              textAlign,
            }}
            ref={currentRef}
          >
            {item.itemId.includes('formula') ? (
              <FormulaColumnContainer
                align={
                  item?.fontStyle?.alignment === 'right'
                    ? 'end'
                    : item?.fontStyle?.alignment === 'center'
                    ? 'center'
                    : 'start'
                }
                fontSize={item?.fontSize || 11}
              >
                <div style={{ textAlign, width: '100%' }}>
                  <span
                    dir={resolveTextDirection(
                      columnValuesMap?.get(item?.itemId)?.text || item?.title,
                    )}
                    style={BIDI_ISOLATE_STYLE}
                  >
                    {columnValuesMap?.get(item?.itemId)?.text ||
                      defaultPreviewer(item.title)}
                  </span>
                </div>
                <StyledTooltip
                  content={t(
                    'Click on preview to see generated formula result',
                  )}
                >
                  <img src={InfoIcon} alt="" className="icon" />
                </StyledTooltip>
              </FormulaColumnContainer>
            ) : ['numbers', 'numeric'].includes(item.type) ? (
              <div style={{ textAlign, width: '100%' }}>
                <span dir={item?.dir || 'ltr'} style={BIDI_ISOLATE_STYLE}>
                  {columnValuesMap?.get(item?.itemId)?.formattedValue ||
                    defaultPreviewer(item.title)}
                </span>
              </div>
            ) : item.type === 'date' ? (
              <div style={{ textAlign, width: '100%' }}>
                <span dir={item?.dir || 'ltr'} style={BIDI_ISOLATE_STYLE}>
                  {columnValuesMap?.get(item?.itemId)?.text
                    ? getFormattedDate(
                        columnValuesMap?.get(item?.itemId)?.text,
                        item.dateFormat.label,
                      )
                    : defaultPreviewer(item.title)}
                </span>
              </div>
            ) : (
              <div
                style={{ whiteSpace: 'pre-line', textAlign, width: '100%' }}
              >
                <span
                  dir={resolveTextDirection(
                    columnValuesMap?.get(item?.itemId)?.text || item?.title,
                  )}
                  style={BIDI_ISOLATE_STYLE}
                >
                  {columnValuesMap?.get(item?.itemId)?.text ||
                    defaultPreviewer(item.title)}
                </span>
              </div>
            )}
          </div>
        </Resizable>
      </PrefilledFieldContainer>
    </>
  );
};

export default PrefilledFieldsEditOverlayChip;
